<template>
  <app-form ref="form" :schema="schema" :layout="layout" :data="formModel" @on-submit="onSubmit"
    @on-input="onFormFieldChanged" @on-input-modified="onFormFieldModified">
    <template v-slot:customer-info-title>
      <app-form-sub-title>{{ $t('actions.form.title.customer-info') }}</app-form-sub-title>
    </template>
    <template v-slot:invoice-info-title>
      <app-form-sub-title>{{ renderInvoiceSubTitle }}</app-form-sub-title>
    </template>
    <template v-if="this.showDeliveryInfo" v-slot:delivery-info-title>
      <app-form-sub-title>{{ $t('actions.form.title.delivery-info') }}</app-form-sub-title>
    </template>
    <template v-slot:additional-info-title>
      <app-form-sub-title>{{ renderAdditionalSubTitle }}</app-form-sub-title>
    </template>
    <template v-slot:additional-info-note>
      <p style="font-size: 13px; margin-bottom: unset" v-html="noticeHTML"></p>
      <div class="label-wrapper">
        <label v-if="prevExplanation.length">Giải trình trước đó</label>
        <br />
        <span>{{ prevExplanation }}</span>
      </div>
    </template>
  </app-form>
</template>
<script>
import { mapState } from 'vuex';
import AppForm from '@/components/app-form/';
import DateTimeUtils from '@/utils/datetime';
import { INVOICE_ACTIONS, BUYER_TYPE } from '@/app-constant';
import { parseFormOption, setSelectValue } from '@/utils/select-options';
import AppFormSubTitle from '@/components/app-form/form-sub-title';
import renderHelper from './render-helper';

export default {
  name: 'ActionsInvoiceForm',
  mixins: [renderHelper],
  components: {
    AppForm,
    AppFormSubTitle,
  },
  props: {
    flag: {
      type: String,
      required: true,
      default: '',
    },
    invoice: {
      type: Object,
      required: true,
    },
    sellerSettingList: {
      type: Object,
      default: () => { },
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initialize();
    });
  },
  data() {
    return {
      schema: [],
      layout: {},
      formModel: {},
      oldData: {},
      fieldsChange: {},
      buyerType: '',
      noticeHTML: '',
      prevExplanation: '',
      showExchangeRate: false,
      showDeliveryInfo: false,
      commonCodeOptions: [],
      invoiceTypeOptions: [],
      templateCodeOptions: [],
      backupLegalName: '',
    };
  },
  computed: {
    ...mapState(['invoiceListEnum', 'activeAnnouncementLines']),

    buyerIdTypeSuggestions() {
      return parseFormOption(this, 'IdentifyTypeEnum');
    },

    paymentMethodOptions() {
      return parseFormOption(this, 'PaymentMethodEnum');
    },

    currencyCodeOptions() {
      return parseFormOption(this, 'CurrencyCodeEnum');
    },

    buyerTypeOptions() {
      return parseFormOption(this, 'BuyerTypeEnum');
    },

    renderFractionDigitsForeignRate() {
      const setting = this.sellerSettingList.qty_rate__settings;
      if (setting && setting.length !== 0 && !this.showExchangeRate) {
        return setting.filter(s => s.key === 'RATE')[0].num_val || 0;
      } return 0;
    },
  },
  methods: {
    initialize() {
      this.setForm();
      this.initFormModel();
      if (this.$refs.form) {
        this.$refs.form.initialize(this.formModel);
      }
    },
    normalizeBuyerData() {
      const invoice = this.invoice.buyer_obj || {};
      const buyerObj = {
        ...invoice,
        ...((this.formModel && this.formModel.buyer_obj) || {}),
      };

      const buyerFields = [
        'buyer_name',
        'buyer_legal_name',
        'buyer_phone_number',
        'buyer_tax_code',
        'buyer_id_no',
        'buyer_passport_no',
        'buyer_email',
        'buyer_address_line',
        'mdvqhnsach',
      ];

      buyerFields.forEach((field) => {
        if (!buyerObj[field] && invoice[field]) {
          buyerObj[field] = invoice[field];
        } else if (!buyerObj[field]) {
          buyerObj[field] = '';
        }
      });

      const tax = buyerObj.buyer_tax_code || '';
      const legal = buyerObj.buyer_legal_name || '';
      const explicitType = invoice.buyer_type || this.formModel.buyer_type;
      const buyerType = explicitType || ((tax && legal) ? BUYER_TYPE.ENTERPRISE : BUYER_TYPE.PERSONAL);

      buyerObj.buyer_type = buyerType;

      this.$set(this.formModel, 'buyer_obj', buyerObj);
      this.formModel.buyer_type = buyerType;
      this.buyerType = buyerType;

      const formVm = this.$refs.form;
      this.$nextTick(() => {
        if (formVm && formVm.setFieldValue) {
          buyerFields.forEach((key) => {
            const val = buyerObj[key] || '';
            formVm.setFieldValue(key, val);
          });
        }
      });
    },

    initFormModel() {
      //Trường hợp hóa đơn giải trình
      if (this.flag === INVOICE_ACTIONS.EXPLAIN_INVOICE) {
        const explanationData = this.invoice.metadata.metadata
        if (explanationData) {
          const explanationInfo = explanationData.explanation_info
          this.prevExplanation = explanationInfo ? explanationInfo.explanation : ''
        }
        return;
      }

      //Merge dữ liệu invoice vào formModel
      this.formModel = Object.assign({}, this.formModel, this.invoice);

      //Nếu là hóa dơn thay thế
      if (this.flag === INVOICE_ACTIONS.ISSUE_REPLACEMENT_INVOICE) {
        this.formModel.agreement_date = new Date(this.formModel.issued_date);
      }

      //Đồng bộ đơn vị tiền tệ
      this.formModel.currency_code = this.formModel.currency_unit;

      //===============================
      // Template info
      //===============================
      const template_package_order = this.invoice && this.invoice.template_package_order;
      const templateTypeObj = template_package_order && template_package_order.template_type_obj;
      if (this.invoice.id_pinvoice && templateTypeObj) {
        this.formModel.invoice_type = templateTypeObj.invoice_type || '';
        this.formModel.template_code = templateTypeObj.template_code || '';
      }

      const templateCode = this.formModel.template_code || '';
      const invoiceType = this.formModel.invoice_type || '';

      this.showDeliveryInfo = invoiceType === '6';
      this.commonCodeOptions = this.getCommonCodeOptions(templateCode);
      this.templateCodeOptions = this.getTemplateCodeOptions(invoiceType);
      this.invoiceTypeOptions = this.getInvoiceTypeOptions();

      // Cập nhật common_code lúc sang năm mới
      this.formModel.invoice_common_code = setSelectValue(this.commonCodeOptions, this.formModel.invoice_common_code);

      // ========================
      // Buyer Info
      // ========================
      this.normalizeBuyerData();

      // Cập nhật giá trị
      if (this.schema.invoice_type) this.$set(this.schema.invoice_type, 'options', this.invoiceTypeOptions);
      if (this.schema.template_code) this.$set(this.schema.template_code, 'options', this.templateCodeOptions);
      if (this.schema.invoice_common_code) this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
      if (this.showDeliveryInfo) {
        this.formModel.NBan_LDDNBo = this.invoice.data_plus_for_phieu_xkvc.NBan_LDDNBo;
        this.formModel.NBan_PTVChuyen = this.invoice.data_plus_for_phieu_xkvc.NBan_PTVChuyen;
        this.formModel.NBan_HDSo = this.invoice.data_plus_for_phieu_xkvc.NBan_HDSo;
        this.formModel.NBan_DChi = this.invoice.data_plus_for_phieu_xkvc.NBan_DChi;
        this.formModel.NBan_TNVChuyen = this.invoice.data_plus_for_phieu_xkvc.NBan_TNVChuyen;
        this.formModel.NBan_HVTNXHang = this.invoice.data_plus_for_phieu_xkvc.NBan_HVTNXHang;
      }

      // save init data and generate notice
      this.oldData = { ...this.formModel };
      this.generateNotice();
    },

    submit() {
      return this.$refs.form.submit();
    },

    validate() {
      return this.$refs.form.validate();
    },

    onFormFieldChanged($event, field) {
      const formVm = this.$refs.form;
      if (!field) {
        return;
      }

      if (field.name === 'buyer_type' && field.value) {
        const buyerType = field.value;
        this.buyerType = buyerType;

        if (!this.formModel.buyer_obj) {
          this.$set(this.formModel, 'buyer_obj', {});
        }

        const buyerObj = { ...this.formModel.buyer_obj };

        if (field.name === 'buyer_type' && field.value) {
          const buyerType = field.value;
          this.buyerType = buyerType;

          if (!this.formModel.buyer_obj) {
            this.$set(this.formModel, 'buyer_obj', {});
          }

          const buyerObj = this.formModel.buyer_obj;

          // chỉ update buyer_type
          this.$set(buyerObj, 'buyer_type', buyerType);
          this.formModel.buyer_type = buyerType;

          if (buyerType === BUYER_TYPE.ENTERPRISE) {
            if (this.backupLegalName) {
              this.formModel.buyer_obj.buyer_legal_name = this.backupLegalName;
              this.$nextTick(() => {
                formVm.setFieldValue("buyer_legal_name", this.backupLegalName, { validate: false });
              });
            }
            this.$set(this.schema.buyer_legal_name, 'disabled', false);
          } else {
            this.backupLegalName = this.formModel.buyer_obj.buyer_legal_name || "";

            this.$set(this.schema.buyer_legal_name, 'disabled', true);

            this.formModel.buyer_obj.buyer_legal_name = "";
            this.$nextTick(() => {
              formVm.setFieldValue("buyer_legal_name", "", { validate: false });
            });
          }

        }

        this.$set(buyerObj, 'buyer_type', buyerType);
        this.formModel.buyer_type = buyerType;
        this.$set(this.formModel, 'buyer_obj', buyerObj);
      }


      if (field.name === 'buyer_id_type' && field.value) {
        const selectedType = field.value;
        if (selectedType.value) {
          formVm.setFieldValue('buyer_id_type', selectedType.value, { validate: false });
        }
      }

      if (field.name === 'invoice_type') {
        if (this.formModel.invoice_type === field.value) return;
        this.formModel.invoice_type = field.value;
        this.showDeliveryInfo = field.value === '6';

        const templateCode = (this.activeAnnouncementLines.list_template_code[this.formModel.invoice_type][0] || '');
        const commonCode = (this.activeAnnouncementLines.list_common_code[templateCode][0] || '');

        this.templateCodeOptions = this.getTemplateCodeOptions(this.formModel.invoice_type);
        this.$set(this.schema.template_code, 'options', this.templateCodeOptions);
        formVm.setFieldValue('template_code', templateCode, { validate: false });

        this.commonCodeOptions = this.getCommonCodeOptions(templateCode);
        this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
        formVm.setFieldValue('invoice_common_code', commonCode, { validate: false });

        formVm.setFieldValue('invoice_type', field.value, { validate: false });

        this.$emit('on-change-invoice-type', field.value);
        if (this.showDeliveryInfo) {
          formVm.setFieldValue('NBan_LDDNBo', null, { validate: false });
          formVm.setFieldValue('NBan_PTVChuyen', null, { validate: false });
          formVm.setFieldValue('NBan_HDSo', null, { validate: false });
          formVm.setFieldValue('NBan_DChi', null, { validate: false });
          formVm.setFieldValue('NBan_TNVChuyen', null, { validate: false });
          formVm.setFieldValue('NBan_HVTNXHang', null, { validate: false });
        }
      }

      if (field.name === 'template_code') {
        if (this.formModel.template_code === field.value) return;
        this.formModel.template_code = field.value;
        const commonCode = (this.activeAnnouncementLines.list_common_code[this.formModel.template_code][0] || '');

        this.commonCodeOptions = this.getCommonCodeOptions(this.formModel.template_code);
        this.$set(this.schema.invoice_common_code, 'options', this.commonCodeOptions);
        formVm.setFieldValue('invoice_common_code', commonCode, { validate: false });

        formVm.setFieldValue('template_code', field.value, { validate: false });
      }

      if (field.name === 'total_amount_without_tax') {
        const vat = formVm.getFieldValue('tax_percentage') || 0;
        formVm.setFieldValue('total_amount_with_tax', `${Math.round(parseInt(field.value, 0) * ((100 + parseInt(vat, 0)) / 100))}`, { validate: false });
      }

      if (field.name === 'currency_code') {
        this.showExchangeRate = field.value !== 'VND';
        if (this.showExchangeRate) {
          if (field.value === this.formModel.currency_code) {
            formVm.setFieldValue('foreign_rate', this.formModel.foreign_rate, { validate: false });
          } else {
            formVm.setFieldValue('foreign_rate', null, { validate: false });
          }
        }
        this.$emit('on-change-currency-code', field.value);
      }

      // cập nhật thay đổi thông tin của invoice
      if (this.flag === INVOICE_ACTIONS.ADJUST_INFO && !['note_change', 'agreement_date', 'reason_change', 'evidence_for_change'].includes(field.name)) {
        const oldValue = this.oldData[field.name];
        if (oldValue !== field.value) {
          this.fieldsChange[field.name] = {
            oldValue,
            newValue: field.name === 'buyer_id_type' ? field.value.value : field.value,
          };
        } else {
          delete this.fieldsChange[field.name];
        }
        formVm.setFieldValue('note_change', this.generateNotice(), { validate: false });
      }
    },

    onFormFieldModified(isChanged) {
      this.$emit('on-input-modified', isChanged);
    },

    onSubmit(formModel, validate, { resolve, reject }) {
      this.$refs.form.validate().then((isValid) => {
        if (!isValid) return;

        const payload = {
          ...formModel,
          buyer_obj: { ...formModel.buyer_obj },
        };

        if (payload.buyer_obj.buyer_type === BUYER_TYPE.PERSONAL) {
          payload.buyer_obj.buyer_legal_name = "";
        }

        const syncFields = [
          'buyer_name',
          'buyer_legal_name',
          'buyer_phone_number',
          'buyer_tax_code',
          'buyer_id_no',
          'buyer_passport_no',
          'buyer_email',
          'buyer_address_line',
          'mdvqhnsach',
        ];

        syncFields.forEach(field => {
          if (payload[field] !== undefined) {
            payload.buyer_obj[field] = payload[field];
            delete payload[field];
          }
        });

        this.$emit('on-submit', payload, validate, { resolve, reject });

      });
    },

    generateNotice() {
      if (!this.getTypeNotice) {
        return '';
      }

      const date = DateTimeUtils.formatDate(this.oldData.issued_date, 'DD/MM/YYYY');
      const result = {
        string: `${this.getTypeNotice} cho hóa đơn điện tử mẫu ${this.oldData.template_code || ''} ký hiệu ${this.oldData.invoice_serial || ''} số ${this.oldData.invoice_number || ''} lập ngày ${date || ''}`,
        html: `Ghi chú: <strong>${this.getTypeNotice}</strong> cho hóa đơn điện tử mẫu <strong>${this.oldData.template_code || ''}</strong> ký hiệu <strong>${this.oldData.invoice_serial || ''}</strong> số <strong>${this.oldData.invoice_number || ''}</strong> lập ngày <strong>${date || ''}</strong>`,
      };

      if (!Object.keys(this.fieldsChange).length) {
        this.noticeHTML = result.html;
        return result.string;
      }

      const changed = {
        string: '',
        html: '',
      };

      Object.entries(this.fieldsChange).forEach(([key, value]) => {
        const { oldValue, newValue } = value;
        const label = this.$t(`actions.form.${key}.label`);
        const arrowHTML = oldValue ? '<i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>' : '';
        const arrow = oldValue ? '→' : '';

        changed.html += `<li><strong>${label}:</strong> ${oldValue} ${arrowHTML} ${newValue}</li>`;
        changed.string += `${label}: ${oldValue} ${arrow} ${newValue}, `;
      });

      changed.string = changed.string.slice(0, -2);

      this.noticeHTML = `${result.html}: <ul>${changed.html}</ul>`;
      return `${result.string}: ${changed.string}`.replace(/\s{2,}/g, ' ');
    },

    getCommonCodeOptions(templateCode) {
      return templateCode ? this.activeAnnouncementLines.list_common_code[templateCode]
        .map((x, idx) => ({ id: idx, text: x, value: x })) : [];
    },

    getTemplateCodeOptions(invoiceType) {
      return invoiceType ?
        this.activeAnnouncementLines.list_template_code[invoiceType]
          .map((x, idx) => ({ id: idx, text: x, value: x })) : [];
    },

    getInvoiceTypeOptions() {
      return this.activeAnnouncementLines.list_invoice_type.map(x =>
        ({ value: x, text: this.invoiceListEnum.InvoiceTypeEnum.find(a => (a.id === x)).lable })) || [];
    },

    getValues() {
      return this.$refs.form.getValues();
    },

    setForm() {
      const configs = this.renderForm();
      if (configs) {
        this.schema = configs.schema;
        this.formModel = configs.formModel;
        this.layout = configs.layout;
      }
    },
  },
  watch: {
    'formModel.buyer_obj': {
      handler(newVal) {
        const formVm = this.$refs.form;
        if (!formVm || !formVm.fields) return;
        if (formVm && formVm.setFieldValue) {
          const fields = [
            'buyer_name',
            'buyer_legal_name',
            'buyer_phone_number',
            'buyer_tax_code',
            'buyer_id_no',
            'buyer_passport_no',
            'buyer_email',
            'buyer_address_line',
            'mdvqhnsach',
          ];
          fields.forEach((key) => {
            if (newVal[key] !== undefined && formVm.getFieldValue(key) !== newVal[key]) {
              formVm.setFieldValue(key, newVal[key] || '', { validate: false });
            }
          });
        }
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/portal-components/invoice/actions-invoice/actions-invoice-form.vue